/**
 * @module bridges
 * @package @geenius/adapters/react-shadcn
 * @description shadcn/Radix-style UI bridges for async adapter loading,
 * adapter errors, and toast notifications. Adapter contracts stay delegated to
 * the React reference variant; this module only owns library-native affordances.
 */

"use client";

import type { LoggerAdapter, LogMeta } from "@geenius/adapters";
import {
  AlertCircle,
  CheckCircle2,
  Info,
  Loader2,
  TriangleAlert,
  X,
} from "lucide-react";
import {
  Component,
  type ErrorInfo,
  type HTMLAttributes,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { cn } from "../lib/utils";

type BridgeSize = "sm" | "md" | "lg";

const loadingSizeClasses: Record<BridgeSize, string> = {
  sm: "gap-2 text-xs",
  md: "gap-3 text-sm",
  lg: "gap-4 text-base",
};

const spinnerSizeClasses: Record<BridgeSize, string> = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
};

export interface LoadingBridgeProps extends HTMLAttributes<HTMLDivElement> {
  /** Main loading label announced to assistive technology. */
  label?: ReactNode;
  /** Optional supporting copy rendered below the label. */
  description?: ReactNode;
  /** Visual density of the loading indicator. */
  size?: BridgeSize;
}

/** shadcn-style loading affordance for async adapter work. */
export function LoadingBridge({
  className,
  description,
  label = "Loading adapters",
  size = "md",
  ...props
}: LoadingBridgeProps) {
  return (
    <div
      aria-live="polite"
      className={cn(
        "flex items-center rounded-md border border-border bg-background px-4 py-3 text-foreground shadow-sm",
        loadingSizeClasses[size],
        className,
      )}
      role="status"
      {...props}
    >
      <Loader2
        aria-hidden="true"
        className={cn("shrink-0 animate-spin", spinnerSizeClasses[size])}
      />
      <span className="grid min-w-0 gap-1">
        <span className="font-medium leading-none">{label}</span>
        {description ? (
          <span className="text-xs leading-snug text-muted-foreground">
            {description}
          </span>
        ) : null}
      </span>
    </div>
  );
}

type ErrorBoundaryFallback =
  | ReactNode
  | ((error: Error, reset: () => void) => ReactNode);

export interface ErrorBoundaryBridgeProps {
  children: ReactNode;
  /** Optional custom fallback node or renderer. */
  fallback?: ErrorBoundaryFallback;
  /** Heading used by the default shadcn-style fallback. */
  title?: ReactNode;
  /** Supporting copy used by the default fallback. */
  description?: ReactNode;
  /** Label for the default reset action. */
  resetLabel?: ReactNode;
  /** Additional class names for the default fallback container. */
  className?: string;
  /** Called when a descendant throws. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Called before local error state is reset. */
  onReset?: () => void;
}

interface ErrorBoundaryBridgeState {
  error: Error | null;
}

/** Error boundary rendered with shadcn-compatible alert and button classes. */
export class ErrorBoundaryBridge extends Component<
  ErrorBoundaryBridgeProps,
  ErrorBoundaryBridgeState
> {
  state: ErrorBoundaryBridgeState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryBridgeState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  private reset = () => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const {
      children,
      className,
      description = "The adapter bridge could not finish the requested work.",
      fallback,
      resetLabel = "Try again",
      title = "Adapter error",
    } = this.props;

    if (!error) {
      return children;
    }

    if (typeof fallback === "function") {
      return fallback(error, this.reset);
    }

    if (fallback) {
      return fallback;
    }

    return (
      <div
        className={cn(
          "rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive shadow-sm",
          className,
        )}
        role="alert"
      >
        <div className="flex gap-3">
          <TriangleAlert aria-hidden="true" className="mt-0.5 h-5 w-5" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="font-medium leading-none">{title}</div>
            <p className="text-sm leading-snug text-destructive/90">
              {description}
            </p>
            <p className="break-words text-xs text-destructive/80">
              {error.message}
            </p>
            <button
              className="inline-flex h-8 items-center justify-center rounded-md border border-destructive/30 bg-background px-3 text-xs font-medium text-destructive shadow-sm transition-colors hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              onClick={this.reset}
              type="button"
            >
              {resetLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export type ToastBridgeVariant =
  | "default"
  | "success"
  | "warning"
  | "destructive";

export interface ToastBridgeProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Controls whether the toast is currently visible. */
  open?: boolean;
  /** Visual treatment matching common shadcn toast variants. */
  variant?: ToastBridgeVariant;
  /** Toast title. */
  title: ReactNode;
  /** Optional supporting message. */
  description?: ReactNode;
  /** Optional action label rendered as a shadcn-style button. */
  actionLabel?: ReactNode;
  /** Called when the optional action is pressed. */
  onAction?: () => void;
  /** Called when the dismiss button is pressed or auto-dismiss fires. */
  onDismiss?: () => void;
  /** Auto-dismiss timeout in milliseconds; `0` disables auto-dismiss. */
  duration?: number;
  /** Optional logger adapter that receives one structured event per visible toast. */
  logger?: LoggerAdapter;
  /** Optional structured metadata merged into the logger event. */
  meta?: LogMeta;
}

const toastVariantClasses: Record<ToastBridgeVariant, string> = {
  default: "border-border bg-background text-foreground",
  success: "border-emerald-500/40 bg-emerald-500/10 text-emerald-950",
  warning: "border-amber-500/40 bg-amber-500/10 text-amber-950",
  destructive:
    "border-destructive/50 bg-destructive text-destructive-foreground",
};

const toastIcons = {
  default: Info,
  success: CheckCircle2,
  warning: AlertCircle,
  destructive: TriangleAlert,
} satisfies Record<ToastBridgeVariant, typeof Info>;

/** shadcn-style toast bridge for adapter telemetry and notifications. */
export function ToastBridge({
  actionLabel,
  className,
  description,
  duration = 0,
  logger,
  meta,
  onAction,
  onDismiss,
  open = true,
  title,
  variant = "default",
  ...props
}: ToastBridgeProps) {
  const [visible, setVisible] = useState(open);
  const titleId = useId();
  const loggedToastKey = useRef<string | null>(null);
  const Icon = toastIcons[variant];

  useEffect(() => {
    setVisible(open);
  }, [open]);

  useEffect(() => {
    if (!visible) {
      loggedToastKey.current = null;
      return;
    }

    if (!logger) {
      return;
    }

    const titleText =
      typeof title === "string" ? title : "Adapter notification";
    const descriptionText =
      typeof description === "string" ? description : undefined;
    const toastKey = `${variant}:${titleText}:${descriptionText ?? ""}`;

    if (loggedToastKey.current === toastKey) {
      return;
    }

    loggedToastKey.current = toastKey;
    const payload: LogMeta = {
      bridge: "react-shadcn",
      variant,
      ...meta,
    };

    if (descriptionText) {
      payload.description = descriptionText;
    }

    if (variant === "destructive") {
      void logger.error(titleText, payload);
      return;
    }

    if (variant === "warning") {
      void logger.warn(titleText, payload);
      return;
    }

    void logger.info(titleText, payload);
  }, [description, logger, meta, title, variant, visible]);

  useEffect(() => {
    if (!visible || duration <= 0) {
      return;
    }

    const timeout = globalThis.setTimeout(() => {
      setVisible(false);
      onDismiss?.();
    }, duration);

    return () => globalThis.clearTimeout(timeout);
  }, [duration, onDismiss, visible]);

  if (!visible) {
    return null;
  }

  const dismiss = () => {
    setVisible(false);
    onDismiss?.();
  };

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        "pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-md border p-4 shadow-lg",
        toastVariantClasses[variant],
        className,
      )}
      role={variant === "destructive" ? "alert" : "status"}
      {...props}
    >
      <Icon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="text-sm font-semibold leading-none" id={titleId}>
          {title}
        </div>
        {description ? (
          <div className="text-sm leading-snug opacity-90">{description}</div>
        ) : null}
        {actionLabel ? (
          <button
            className="mt-2 inline-flex h-8 items-center justify-center rounded-md border border-current/20 bg-transparent px-3 text-xs font-medium transition-colors hover:bg-current/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={onAction}
            type="button"
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
      <button
        aria-label="Dismiss notification"
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={dismiss}
        type="button"
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </section>
  );
}
